import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { 
  Users, Mail, Building, Phone, Calendar, Copy, Check, Loader2, Trash2, 
  Eye, X, ExternalLink, MapPin, Briefcase, MessageSquare, Filter,
  ChevronDown, ArrowUpDown, Clock, CheckCircle, XCircle, UserCheck,
  Sparkles, RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { API_URL } from "../utils/api";

// Status configurations
const STATUS_CONFIG = {
  partial: { label: "Ofullständig", color: "gray", icon: Clock },
  new: { label: "Ny", color: "blue", icon: Sparkles },
  contacted: { label: "Kontaktad", color: "yellow", icon: UserCheck },
  qualified: { label: "Kvalificerad", color: "purple", icon: CheckCircle },
  won: { label: "Vunnen", color: "green", icon: CheckCircle },
  lost: { label: "Förlorad", color: "red", icon: XCircle }
};

// Score configurations
const SCORE_CONFIG = {
  high: { label: "Hög", color: "green" },
  medium: { label: "Medium", color: "yellow" },
  low: { label: "Låg", color: "gray" }
};

// Intent configurations
const INTENT_CONFIG = {
  ready: { label: "Vill göra klart", color: "green" },
  email_only: { label: "Endast e-post", color: "blue" },
  changes: { label: "Vill ändra", color: "yellow" },
  meeting: { label: "Vill boka möte", color: "blue" },
  unknown: { label: "Okänd", color: "gray" }
};

// Event type icons and colors
const EVENT_ICONS = {
  email_sent: { icon: Mail, color: "blue" },
  email_opened: { icon: Eye, color: "green" },
  email_failed: { icon: XCircle, color: "red" },
  configurator_link_opened: { icon: ExternalLink, color: "blue" },
  configurator_submitted: { icon: CheckCircle, color: "green" },
  primary_cta_clicked: { icon: CheckCircle, color: "green" },
  secondary_cta_clicked: { icon: Mail, color: "blue" },
  demo_generated: { icon: Sparkles, color: "purple" },
  customer_confirmed: { icon: UserCheck, color: "green" },
  error_email: { icon: XCircle, color: "red" },
  error_token: { icon: XCircle, color: "red" },
  default: { icon: Clock, color: "gray" }
};

// Status Indicator component for lead journey
function StatusIndicator({ label, active, timestamp, warning }) {
  const formatTime = (ts) => {
    if (!ts) return null;
    try {
      const date = new Date(ts);
      return date.toLocaleString("sv-SE", { 
        month: "short", 
        day: "numeric", 
        hour: "2-digit", 
        minute: "2-digit" 
      });
    } catch {
      return null;
    }
  };
  
  return (
    <div className={`p-2 rounded-lg border ${
      active 
        ? "bg-green-50 border-green-200" 
        : warning 
          ? "bg-yellow-50 border-yellow-200"
          : "bg-gray-50 border-gray-200"
    }`}>
      <div className="flex items-center gap-2">
        {active ? (
          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
        ) : warning ? (
          <Clock className="w-4 h-4 text-yellow-600 flex-shrink-0" />
        ) : (
          <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
        <span className={`text-xs font-medium ${
          active ? "text-green-700" : warning ? "text-yellow-700" : "text-gray-500"
        }`}>
          {label}
        </span>
      </div>
      {timestamp && (
        <p className="text-xs text-gray-500 mt-1 ml-6">{formatTime(timestamp)}</p>
      )}
    </div>
  );
}

export default function ChatbotLeads({ clientId: clientIdProp } = {}) {
  const { getAuthHeaders } = useAuth();
  // H3.2.3 — accept ?client_id=X from URL to scope the view to a single
  // tenant. Without the param we show the full Elido admin pipeline.
  // Fas C — when rendered EMBEDDED inside Kund 360 the client id comes from
  // a prop (path param). In embedded mode we never read/write the shared URL
  // and never fall back to the global pipeline.
  const [searchParams, setSearchParams] = useSearchParams();
  const embedded = !!clientIdProp;
  const clientIdFilter = clientIdProp || searchParams.get("client_id") || "";
  const [leads, setLeads] = useState([]);
  const [visibleCount, setVisibleCount] = useState(25);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [copiedText, setCopiedText] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [scoreFilter, setScoreFilter] = useState("");
  const [runtimeFilter, setRuntimeFilter] = useState("");   // H3 — ""|"blueprint"|"legacy"
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [showFilters, setShowFilters] = useState(false);
  
  // Status update
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const loadLeads = useCallback(async () => {
    try {
      let url = `${API_URL}/api/admin/leads?`;
      if (clientIdFilter) url += `client_id=${encodeURIComponent(clientIdFilter)}&`;
      if (statusFilter) url += `status=${statusFilter}&`;
      if (scoreFilter) url += `lead_score=${scoreFilter}&`;
      if (runtimeFilter) url += `runtime=${runtimeFilter}&`;
      
      const response = await fetch(url, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        let sortedLeads = data.leads || [];
        
        // Client-side sorting
        sortedLeads.sort((a, b) => {
          let aVal = a[sortBy];
          let bVal = b[sortBy];
          
          if (sortBy === "created_at" || sortBy === "updated_at") {
            aVal = new Date(aVal || 0).getTime();
            bVal = new Date(bVal || 0).getTime();
          }
          
          if (sortOrder === "asc") {
            return aVal > bVal ? 1 : -1;
          }
          return aVal < bVal ? 1 : -1;
        });
        
        setLeads(sortedLeads);
        setVisibleCount(25); // återställ paginering när filter/data ändras
      }
    } catch (error) {
      console.error("Failed to load leads:", error);
      toast.error("Kunde inte ladda leads");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, clientIdFilter, statusFilter, scoreFilter, runtimeFilter, sortBy, sortOrder]);

  const loadStats = useCallback(async () => {
    try {
      // H3.2.3 — KPI tiles must respect the same client_id filter that
      // the lead table uses, otherwise the banner says "Filterad till
      // kund: X" while the numbers above still count Elido globally.
      let url = `${API_URL}/api/admin/leads/stats/summary`;
      if (clientIdFilter) {
        url += `?client_id=${encodeURIComponent(clientIdFilter)}`;
      }
      const response = await fetch(url, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error("Failed to load stats:", error);
    }
  }, [getAuthHeaders, clientIdFilter]);

  useEffect(() => {
    loadLeads();
    loadStats();
  }, [loadLeads, loadStats]);

  const deleteLead = async (leadId) => {
    if (!window.confirm("Är du säker på att du vill radera denna lead?")) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/leads/${leadId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        toast.success("Lead raderad");
        loadLeads();
        loadStats();
        if (selectedLead?.id === leadId) {
          setSelectedLead(null);
        }
      }
    } catch (error) {
      toast.error("Kunde inte radera lead");
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    setUpdatingStatus(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/leads/${leadId}/status`, {
        method: "PUT",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json"
        },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Status ändrad till "${STATUS_CONFIG[newStatus]?.label || newStatus}"`);
        
        // Update local state
        setLeads(prev => prev.map(l => l.id === leadId ? data.lead : l));
        if (selectedLead?.id === leadId) {
          setSelectedLead(data.lead);
        }
        loadStats();
      } else {
        toast.error("Kunde inte uppdatera status");
      }
    } catch (error) {
      toast.error("Kunde inte uppdatera status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(id);
      toast.success("Kopierat!");
      setTimeout(() => setCopiedText(null), 2000);
    } catch (error) {
      toast.error("Kunde inte kopiera");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("sv-SE", {
      month: "short",
      day: "numeric"
    });
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const clearFilters = () => {
    setStatusFilter("");
    setScoreFilter("");
    setRuntimeFilter("");
  };

  const hasActiveFilters = statusFilter || scoreFilter || runtimeFilter;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* H3.3 — Elido Sales-scope banner. När ingen client_id är satt visar
          vyn Elidos interna säljpipeline (gratis-analys, demo, offert,
          onboarding). Widget-leads per kund bor i Kund 360-fliken. */}
      {!clientIdFilter && (
        <div
          className="bg-[#0F2A30]/5 border border-[#0F2A30]/15 rounded-lg px-4 py-3 flex items-start justify-between gap-3 flex-wrap"
          data-testid="leads-elido-sales-banner"
        >
          <div className="flex items-start gap-2 text-sm text-[#0F2A30]">
            <Briefcase className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">Elido Sales Leads</div>
              <p className="text-xs text-gray-600 mt-0.5 max-w-2xl">
                Elidos interna säljpipeline: gratis-analys, demo-bokning, offert,
                onboarding och konfigurator. Widget-/chatbot-leads per kund visas
                i respektive Kund 360 &rarr; <em>Leads &amp; konversationer</em>-flik.
              </p>
            </div>
          </div>
          <Link
            to="/admin/clients"
            className="text-xs text-[#255E69] hover:underline whitespace-nowrap"
            data-testid="leads-goto-kunder"
          >
            Öppna Kunder (Kund 360) →
          </Link>
        </div>
      )}

      {/* H3.2.3 — sticky banner when scoped to a single client */}
      {clientIdFilter && (
        <div
          className="bg-[#255E69]/5 border border-[#255E69]/20 rounded-lg px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
          data-testid="leads-client-scope-banner"
        >
          <div className="flex items-center gap-2 text-sm text-[#255E69]">
            <Filter className="w-4 h-4" />
            Filterad till kund: <code className="font-mono text-xs bg-white/60 px-1.5 py-0.5 rounded">{clientIdFilter}</code>
          </div>
          {!embedded && (
            <div className="flex items-center gap-2">
              <Link
                to={`/admin/clients/${encodeURIComponent(clientIdFilter)}`}
                className="text-xs text-[#255E69] hover:underline"
                data-testid="leads-back-to-kund360"
              >
                Tillbaka till Kund 360 →
              </Link>
              <button
                onClick={() => setSearchParams({})}
                className="text-xs text-gray-500 hover:text-gray-700"
                data-testid="leads-clear-client-filter"
              >
                Visa alla leads
              </button>
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-3" data-testid="leads-page-title">
            <Users className="w-6 sm:w-7 h-6 sm:h-7 text-[#255E69]" />
            {clientIdFilter ? "Widget-leads" : "Elido Sales Leads"}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {clientIdFilter
              ? "Chatbot-/widget-leads för vald kund"
              : "Elidos interna säljpipeline (gratis-analys, demo, offert)"}
          </p>
        </div>
        <button
          onClick={() => { loadLeads(); loadStats(); }}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-[#255E69] hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Uppdatera
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4">
            <div className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-xs sm:text-sm text-gray-500">Totalt</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4">
            <div className="text-xl sm:text-2xl font-bold text-blue-600">{stats.by_status?.new || 0}</div>
            <div className="text-xs sm:text-sm text-gray-500">Nya</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4">
            <div className="text-xl sm:text-2xl font-bold text-yellow-600">{stats.by_status?.contacted || 0}</div>
            <div className="text-xs sm:text-sm text-gray-500">Kontaktade</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4">
            <div className="text-xl sm:text-2xl font-bold text-green-600">{stats.by_status?.won || 0}</div>
            <div className="text-xs sm:text-sm text-gray-500">Vunna</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4">
            <div className="text-xl sm:text-2xl font-bold text-green-600">{stats.qualified}</div>
            <div className="text-xs sm:text-sm text-gray-500">Kvalificerade</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 text-sm font-medium text-gray-700"
        >
          <Filter className="w-4 h-4" />
          Filter & Sortering
          <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? "rotate-180" : ""}`} />
          {hasActiveFilters && (
            <span className="px-2 py-0.5 bg-[#255E69] text-white text-xs rounded-full">Aktiva</span>
          )}
        </button>
        
        {showFilters && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#255E69]/20"
                data-testid="leads-status-filter"
              >
                <option value="">Alla statusar</option>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Lead Score</label>
              <select
                value={scoreFilter}
                onChange={(e) => setScoreFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#255E69]/20"
                data-testid="leads-score-filter"
              >
                <option value="">Alla poäng</option>
                {Object.entries(SCORE_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Runtime</label>
              <select
                value={runtimeFilter}
                onChange={(e) => setRuntimeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#255E69]/20"
                data-testid="leads-runtime-filter"
              >
                <option value="">Alla runtimes</option>
                <option value="blueprint">Blueprint</option>
                <option value="legacy">Legacy</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Sortera efter</label>
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split("-");
                  setSortBy(field);
                  setSortOrder(order);
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#255E69]/20"
              >
                <option value="created_at-desc">Nyast först</option>
                <option value="created_at-asc">Äldst först</option>
                <option value="company_name-asc">Företag A-Ö</option>
                <option value="lead_score-desc">Högst poäng först</option>
              </select>
            </div>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-red-500 hover:text-red-700"
              >
                Rensa filter
              </button>
            )}
          </div>
        )}
      </div>

      {/* Leads List/Table */}
      {leads.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 sm:p-12 text-center">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {hasActiveFilters ? "Inga leads matchar filtren" : "Inga leads ännu"}
          </h3>
          <p className="text-gray-500 text-sm">
            {hasActiveFilters 
              ? "Försök justera dina filter eller rensa dem."
              : "När besökare använder chatboten visas deras leads här."
            }
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="mt-4 text-sm text-[#255E69] hover:underline"
            >
              Rensa filter
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="sm:hidden space-y-3">
            {leads.slice(0, visibleCount).map((lead) => (
              <LeadCard 
                key={lead.id} 
                lead={lead}
                onView={() => setSelectedLead(lead)}
                onDelete={() => deleteLead(lead.id)}
                onCopy={copyToClipboard}
                copiedText={copiedText}
                formatDateShort={formatDateShort}
              />
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th 
                      className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleSort("company_name")}
                    >
                      <span className="flex items-center gap-1">
                        Företag <ArrowUpDown className="w-3 h-3" />
                      </span>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Bransch</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Stad</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Score</th>
                    <th 
                      className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleSort("created_at")}
                    >
                      <span className="flex items-center gap-1">
                        Datum <ArrowUpDown className="w-3 h-3" />
                      </span>
                    </th>
                    <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Åtgärder</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {leads.slice(0, visibleCount).map((lead) => (
                    <LeadTableRow
                      key={lead.id}
                      lead={lead}
                      onView={() => setSelectedLead(lead)}
                      onDelete={() => deleteLead(lead.id)}
                      formatDate={formatDate}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Paginering — "Ladda fler" (klientsidig). */}
      {leads.length > visibleCount && (
        <div className="flex items-center justify-center pt-4" data-testid="leads-pagination">
          <button
            onClick={() => setVisibleCount((n) => n + 25)}
            data-testid="leads-load-more"
            className="px-4 py-2 text-sm font-medium text-[#255E69] border border-gray-200 rounded-full hover:bg-gray-50"
          >
            Ladda fler ({leads.length - visibleCount} kvar)
          </button>
        </div>
      )}

      {/* Lead Detail Modal */}
      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onStatusChange={updateLeadStatus}
          onDelete={deleteLead}
          onCopy={copyToClipboard}
          copiedText={copiedText}
          formatDate={formatDate}
          updatingStatus={updatingStatus}
        />
      )}
    </div>
  );
}

// Status Badge Component
function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.partial;
  const Icon = config.icon;
  
  const colorClasses = {
    gray: "bg-gray-100 text-gray-700",
    blue: "bg-blue-100 text-blue-700",
    yellow: "bg-yellow-100 text-yellow-700",
    green: "bg-green-100 text-green-700",
    red: "bg-red-100 text-red-700"
  };
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${colorClasses[config.color]}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

// Score Badge Component
function ScoreBadge({ score }) {
  const config = SCORE_CONFIG[score] || SCORE_CONFIG.low;
  
  const colorClasses = {
    gray: "bg-gray-100 text-gray-700",
    yellow: "bg-yellow-100 text-yellow-700",
    green: "bg-green-100 text-green-700"
  };
  
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClasses[config.color]}`}>
      {config.label}
    </span>
  );
}

// Intent Badge Component
function IntentBadge({ intent }) {
  const config = INTENT_CONFIG[intent] || INTENT_CONFIG.unknown;
  
  const colorClasses = {
    gray: "bg-gray-100 text-gray-700",
    blue: "bg-blue-100 text-blue-700",
    yellow: "bg-yellow-100 text-yellow-700",
    green: "bg-green-100 text-green-700"
  };
  
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClasses[config.color]}`}>
      {config.label}
    </span>
  );
}

// H3 — Runtime Badge (blueprint / legacy)
function RuntimeBadge({ lead, className = "" }) {
  const runtime = lead?.runtime || lead?.metadata?.runtime;
  if (!runtime) return null;
  const isBp = runtime === "blueprint";
  const cls = isBp
    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : "bg-slate-100 text-slate-700 border-slate-200";
  const label = isBp
    ? `Blueprint${lead.blueprint_version ? ` v${lead.blueprint_version}` : ""}`
    : "Legacy";
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border ${cls} ${className}`}
      data-testid={`runtime-badge-${lead.id}`}
      title={`Runtime: ${runtime}${lead.blueprint_id ? ` · ${lead.blueprint_id.slice(0, 8)}` : ""}`}
    >
      {label}
    </span>
  );
}


// Lead Card for Mobile
function LeadCard({ lead, onView, onDelete, onCopy, copiedText, formatDateShort }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid={`lead-card-${lead.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#255E69]/10 rounded-full flex items-center justify-center flex-shrink-0">
            <Building className="w-5 h-5 text-[#255E69]" />
          </div>
          <div>
            <p className="font-medium text-gray-900">{lead.company_name || lead.customer_name || "Okänt företag"}</p>
            <p className="text-sm text-gray-500">{lead.industry || lead.handyman_name || "Ingen bransch"}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="text-xs text-gray-400">{formatDateShort(lead.created_at)}</span>
          <StatusBadge status={lead.status} />
          <RuntimeBadge lead={lead} />
        </div>
      </div>
      
      {lead.city && (
        <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
          <MapPin className="w-4 h-4 text-gray-400" />
          <span>{lead.city}</span>
        </div>
      )}
      
      {lead.email && (
        <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
          <Mail className="w-4 h-4 text-gray-400" />
          <span className="truncate flex-1">{lead.email}</span>
          <button
            onClick={() => onCopy(lead.email, `email-${lead.id}`)}
            className="p-1.5 text-gray-400 hover:text-[#255E69]"
          >
            {copiedText === `email-${lead.id}` ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </button>
        </div>
      )}
      
      {lead.demo_url && (
        <div className="mt-2">
          <a
            href={lead.demo_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-[#255E69] hover:underline"
          >
            <ExternalLink className="w-4 h-4" />
            Se demo
          </a>
        </div>
      )}
      
      <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2">
        <button
          onClick={onView}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-[#255E69] bg-[#255E69]/10 rounded-lg"
          data-testid={`view-lead-${lead.id}`}
        >
          <Eye className="w-4 h-4" />
          Visa
        </button>
        {lead.email && (
          <a
            href={`mailto:${lead.email}`}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-white bg-[#255E69] rounded-lg"
          >
            <Mail className="w-4 h-4" />
            E-posta
          </a>
        )}
        <button
          onClick={onDelete}
          className="px-3 py-2 text-red-500 hover:bg-red-50 rounded-lg"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// Lead Table Row for Desktop
function LeadTableRow({ lead, onView, onDelete, formatDate }) {
  return (
    <tr className="hover:bg-gray-50" data-testid={`lead-row-${lead.id}`}>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#255E69]/10 rounded-full flex items-center justify-center">
            <Building className="w-4 h-4 text-[#255E69]" />
          </div>
          <div>
            <span className="font-medium text-gray-900">{lead.company_name || lead.customer_name || "Okänt"}</span>
            <RuntimeBadge lead={lead} className="ml-2" />
            {lead.demo_url && (
              <a
                href={lead.demo_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-xs text-[#255E69] hover:underline"
              >
                Se demo
              </a>
            )}
          </div>
        </div>
      </td>
      <td className="py-3 px-4 text-gray-600">{lead.industry || "-"}</td>
      <td className="py-3 px-4 text-gray-600">{lead.city || "-"}</td>
      <td className="py-3 px-4"><StatusBadge status={lead.status} /></td>
      <td className="py-3 px-4"><ScoreBadge score={lead.lead_score} /></td>
      <td className="py-3 px-4 text-gray-500 text-sm">{formatDate(lead.created_at)}</td>
      <td className="py-3 px-4">
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={onView}
            className="p-2 text-gray-400 hover:text-[#255E69] transition-colors"
            title="Visa detaljer"
            data-testid={`view-lead-${lead.id}`}
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-gray-400 hover:text-red-500 transition-colors"
            title="Radera"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// Lead Detail Modal
function LeadDetailModal({ lead, onClose, onStatusChange, onDelete, onCopy, copiedText, formatDate, updatingStatus }) {
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl shadow-xl max-h-[90vh] overflow-auto" data-testid="lead-detail-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100 sticky top-0 bg-white">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Lead detaljer</h2>
            <p className="text-sm text-gray-500">{lead.company_name || "Okänt företag"}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 sm:p-6 space-y-6">
          {/* Status and Score */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                disabled={updatingStatus}
              >
                <StatusBadge status={lead.status} />
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>
              
              {showStatusDropdown && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <button
                      key={key}
                      onClick={() => {
                        onStatusChange(lead.id, key);
                        setShowStatusDropdown(false);
                      }}
                      className={`w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 ${
                        lead.status === key ? "bg-gray-50" : ""
                      }`}
                    >
                      <StatusBadge status={key} />
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <ScoreBadge score={lead.lead_score} />
            {lead.intent && <IntentBadge intent={lead.intent} />}
            <RuntimeBadge lead={lead} />
            {lead.notification_sent && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                <Mail className="w-3 h-3" />
                Notifierad
              </span>
            )}
            {lead.source === "blueprint-widget-chat" && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="lead-source-blueprint-chat">
                Källa: Blueprint Widget Chat
              </span>
            )}
          </div>
          
          {/* Company Info */}
          <div className="grid gap-3">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Företagsinformation</h3>
            
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Building className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Företag</p>
                  <p className="text-gray-900 font-medium">{lead.company_name || "-"}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Briefcase className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Bransch</p>
                  <p className="text-gray-900">{lead.industry || "-"}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Stad</p>
                  <p className="text-gray-900">{lead.city || "-"}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Calendar className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Skapad</p>
                  <p className="text-gray-900">{formatDate(lead.created_at)}</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Contact Info */}
          {(lead.email || lead.phone) && (
            <div className="grid gap-3">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Kontaktuppgifter</h3>
              
              <div className="grid sm:grid-cols-2 gap-3">
                {lead.email && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500">E-post</p>
                      <p className="text-gray-900 truncate">{lead.email}</p>
                    </div>
                    <button
                      onClick={() => onCopy(lead.email, `modal-email-${lead.id}`)}
                      className="p-2 text-gray-400 hover:text-[#255E69] transition-colors flex-shrink-0"
                    >
                      {copiedText === `modal-email-${lead.id}` ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                )}
                
                {lead.phone && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500">Telefon</p>
                      <p className="text-gray-900">{lead.phone}</p>
                    </div>
                    <button
                      onClick={() => onCopy(lead.phone, `modal-phone-${lead.id}`)}
                      className="p-2 text-gray-400 hover:text-[#255E69] transition-colors flex-shrink-0"
                    >
                      {copiedText === `modal-phone-${lead.id}` ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Attribution */}
          {(lead.source || lead.page_url || lead.gclid) && (
            <div className="grid gap-3">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Attribution</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {lead.source && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">Källa</p>
                    <p className="text-sm font-medium text-gray-900">{lead.source}</p>
                  </div>
                )}
                {lead.page_url && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">Sida</p>
                    <p className="text-sm font-medium text-gray-900 truncate">{lead.page_url}</p>
                  </div>
                )}
                {lead.gclid && (
                  <div className="p-3 bg-orange-50 rounded-lg border border-orange-200" data-testid="lead-gclid">
                    <p className="text-xs text-orange-600">Google Ads (gclid)</p>
                    <p className="text-sm font-mono text-gray-900 truncate">{lead.gclid}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Demo Link */}
          {lead.demo_url && (
            <div className="grid gap-3">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Demo</h3>
              <a
                href={lead.demo_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-[#255E69]/5 border border-[#255E69]/20 rounded-lg text-[#255E69] hover:bg-[#255E69]/10 transition-colors"
                data-testid="demo-link"
              >
                <ExternalLink className="w-5 h-5" />
                <span className="truncate flex-1">{lead.demo_url}</span>
              </a>
            </div>
          )}
          
          {/* Lead Journey Status */}
          <div className="grid gap-3">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Kundresa</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <StatusIndicator 
                label="E-post skickad" 
                active={lead.email_sent_at || lead.notification_sent} 
                timestamp={lead.email_sent_at}
              />
              <StatusIndicator 
                label="E-post öppnad" 
                active={lead.email_opened_at} 
                timestamp={lead.email_opened_at}
              />
              <StatusIndicator 
                label="Konfigurator öppnad" 
                active={lead.configurator_opened_at} 
                timestamp={lead.configurator_opened_at}
              />
              <StatusIndicator 
                label="Konfiguration skickad" 
                active={lead.configuration_submitted_at} 
                timestamp={lead.configuration_submitted_at}
              />
              <StatusIndicator 
                label="Kund bekräftad" 
                active={lead.customer_confirmed} 
              />
              <StatusIndicator 
                label="Token giltig" 
                active={lead.configurator_token && !lead.token_expired}
                warning={lead.token_expires_at && new Date(lead.token_expires_at) < new Date()}
              />
            </div>
          </div>
          
          {/* Requested Changes */}
          {lead.requested_changes && (
            <div className="grid gap-3">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Önskade ändringar</h3>
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <MessageSquare className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-900 whitespace-pre-wrap">{lead.requested_changes}</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Events Timeline */}
          {lead.events && lead.events.length > 0 && (
            <div className="grid gap-3">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Händelselogg ({lead.events.length})
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                {lead.events.slice().reverse().map((event, index) => {
                  const eventConfig = EVENT_ICONS[event.event_type] || EVENT_ICONS.default;
                  const EventIcon = eventConfig.icon;
                  const colorClass = {
                    green: "text-green-600 bg-green-100",
                    blue: "text-blue-600 bg-blue-100",
                    red: "text-red-600 bg-red-100",
                    purple: "text-purple-600 bg-purple-100",
                    yellow: "text-yellow-600 bg-yellow-100",
                    gray: "text-gray-600 bg-gray-100"
                  }[eventConfig.color] || "text-gray-600 bg-gray-100";
                  
                  return (
                    <div key={event.id || index} className={`flex items-start gap-3 p-2 rounded-lg ${
                      event.success === false ? "bg-red-50" : "hover:bg-gray-50"
                    }`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                        <EventIcon className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${event.success === false ? "text-red-700" : "text-gray-900"}`}>
                          {event.description}
                        </p>
                        <p className="text-xs text-gray-500">{formatDate(event.timestamp)}</p>
                        {event.data && Object.keys(event.data).length > 0 && (
                          <details className="mt-1">
                            <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
                              Visa detaljer
                            </summary>
                            <pre className="mt-1 text-xs text-gray-500 bg-gray-100 rounded p-2 overflow-x-auto">
                              {JSON.stringify(event.data, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-4 sm:p-6 border-t border-gray-100 flex flex-col sm:flex-row gap-3 sticky bottom-0 bg-white">
          {lead.email && (
            <a
              href={`mailto:${lead.email}`}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#255E69] text-white rounded-lg font-medium hover:bg-[#1e4a53] transition-colors"
            >
              <Mail className="w-4 h-4" />
              Skicka e-post
            </a>
          )}
          {lead.demo_url && (
            <a
              href={lead.demo_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white border border-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Öppna demo
            </a>
          )}
          <button
            onClick={() => onDelete(lead.id)}
            className="px-4 py-3 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
